import React, { forwardRef, useContext } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import { useUniqueId } from "../hooks/useUniqueId";
import { ThemeContext } from "../Theme";
import { RadioButtonContext } from "./RadioButtonContext";
import RadioButtonGroup from "./RadioButtonGroup";

import styles from "./RadioButton.scss";
import RadioButtonLegend from "./RadioButtonLegend";

export const RadioButton = forwardRef((props, ref) => {
  const {
    children,
    value: val,
    disabled,
    isValid,
    checked,
    customClass,
    ...restProps
  } = props;
  const { id } = restProps;
  const group = useContext(RadioButtonContext) || {};
  const buttonId =
    id ||
    ["radio", group.id, group.buttonId || useUniqueId("RadioButton", props) + 1]
      .filter((part) => part)
      .join("-");
  const themeContext = useContext(ThemeContext);
  const checkedProp = {
    [group.onRadioClick ? "checked" : "defaultChecked"]:
      group.checkedId === "" ? checked : group.checkedId === buttonId,
  };

  return (
    <label
      ref={ref}
      className={cx(
        styles.radio,
        {
          [styles[themeContext]]: themeContext,
          "is-disabled": disabled,
        },
        customClass
      )}
      htmlFor={buttonId}
    >
      <input
        value={val}
        type="radio"
        id={buttonId}
        name={group.name || buttonId}
        {...checkedProp}
        disabled={disabled}
        onChange={group.onRadioClick}
        className={cx({ "is-invalid": !isValid })}
        {...restProps}
      />
      <span className={styles.ci} aria-hidden="true" data-testid="toggle" />
      {children}
    </label>
  );
});

RadioButton.propTypes = {
  value: PropTypes.string,
  isValid: PropTypes.bool,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  customClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

RadioButton.defaultProps = {
  value: "",
  isValid: true,
  checked: false,
  disabled: false,
  customClass: "",
};

RadioButton.Group = RadioButtonGroup;
RadioButton.Legend = RadioButtonLegend;

RadioButton.displayName = "RadioButton";

export default RadioButton;
